import { useEffect, useMemo, useState } from 'react'
import { CreditCard, TrendingUp, AlertTriangle, Edit3, Check, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toast } from '@/lib/hooks/use-toast'
import { MaskInputDin } from '@/components/ui/mask-input-din'
import { formatCurrency } from '../../utils'
import { useFetchResumeByIdApuracao } from '../../services'
import { useUpdateResumes } from '../../services/useUpdateResumes'
import { useCurrentUser } from '@/features/auth/state/auth'

type ResumoSimplesProps = {
  id_apuracao?: string
}

export default function ResumoSimples({ id_apuracao }: ResumoSimplesProps) {
  const { data: resumeData } = useFetchResumeByIdApuracao(id_apuracao)

  const { value_rest, id_value_rest, debit, id_debit, credit_apurado } = useMemo(() => {
    const restItem = resumeData?.find((r: { typeValue: string }) => r.typeValue === 'VALOR_REST')
    const debitItem = resumeData?.find((r: { typeValue: string }) => r.typeValue === 'DEBITO')
    const creditItem = resumeData?.find((r: { typeValue: string }) => r.typeValue === 'CREDITO_APURADO')
    return {
      value_rest: restItem?.value ?? 0,
      id_value_rest: restItem?.id ?? '',
      debit: debitItem?.value ?? 0,
      id_debit: debitItem?.id ?? '',
      credit_apurado: creditItem?.value ?? 0
    }
  }, [resumeData])

  const user = useCurrentUser()
  const canEdit = user?.typeUser === 'ADMIN'

  // Estado numérico consolidado
  const [restValue, setRestValue] = useState<number>(value_rest)
  const [debitValue, setDebitValue] = useState<number>(debit)

  // Estado temporário de string para edição
  const [tempRestValue, setTempRestValue] = useState<string>(
    value_rest.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  )
  const [tempDebitValue, setTempDebitValue] = useState<string>(
    debit.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  )

  // Sincroniza quando resumeData mudar
  useEffect(() => {
    setRestValue(value_rest)
    setDebitValue(debit)
    setTempRestValue(value_rest.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
    setTempDebitValue(debit.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
  }, [value_rest, debit])

  // Flags de edição
  const [isEditingRest, setIsEditingRest] = useState<boolean>(false)
  const [isEditingDebit, setIsEditingDebit] = useState<boolean>(false)

  const useUpdateResume = useUpdateResumes()

  const handleSaveRest = async () => {
    if (!canEdit) return
    const parsed = Number(tempRestValue.replace(/\./g, '').replace(',', '.'))
    if (isNaN(parsed)) {
      toast({ title: 'Valor inválido', description: 'Por favor, insira um valor válido.', variant: 'destructive' })
      return
    }
    setRestValue(parsed)
    setIsEditingRest(false)

    await handleUpdateValue(id_value_rest, parsed)

    toast({ title: 'Valor atualizado', description: `Valor restituído atualizado para R$ ${formatCurrency(parsed)}` })
  }

  const handleSaveDebit = async () => {
    if (!canEdit) return
    const parsed = Number(tempDebitValue.replace(/\./g, '').replace(',', '.'))
    if (isNaN(parsed)) {
      toast({ title: 'Valor inválido', description: 'Por favor, insira um valor válido.', variant: 'destructive' })
      return
    }
    setDebitValue(parsed)
    setIsEditingDebit(false)

    await handleUpdateValue(id_debit, parsed)

    toast({ title: 'Valor atualizado', description: `Débito atualizado para R$ ${formatCurrency(parsed)}` })
  }

  const handleCancelRest = () => {
    setTempRestValue(restValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
    setIsEditingRest(false)
  }

  const handleCancelDebit = () => {
    setTempDebitValue(debitValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
    setIsEditingDebit(false)
  }

  const handleUpdateValue = async (id: string, valor: number) => {
    if (!canEdit) return
    if (!id) {
      console.error('ID inexistente para atualização de resumo.')
      return
    }
    try {
      await useUpdateResume.mutateAsync({ id, valor })
    } catch (error) {
      console.error('Erro ao atualizar valor:', error)
      toast({
        title: 'Erro ao atualizar',
        description: 'Não foi possível salvar o valor. Tente novamente.',
        variant: 'destructive'
      })
    }
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      {/* Crédito Apurado (somente leitura para todos) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-green-700">Crédito Apurado</h3>
          <TrendingUp className="w-5 h-5 text-green-600" />
        </div>
        <p className="text-3xl font-bold text-green-600 mb-1">{formatCurrency(credit_apurado)}</p>
        <p className="text-sm text-gray-600">Total de créditos calculados</p>
      </div>

      {/* Valor Restituído */}
      <div
        className={[
          'bg-white rounded-xl shadow-sm border-2 p-6 transition-all duration-200 relative',
          canEdit ? 'cursor-pointer' : 'cursor-default',
          isEditingRest ? 'border-blue-300 shadow-lg' : 'border-blue-200 hover:border-blue-300 hover:shadow-md'
        ].join(' ')}
        onClick={() => canEdit && !isEditingRest && setIsEditingRest(true)}
        aria-disabled={!canEdit}
      >
        {!isEditingRest && canEdit && (
          <div className="absolute top-2 right-2 bg-blue-100 rounded-full p-1">
            <Edit3 className="w-3 h-3 text-blue-600" />
          </div>
        )}

        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-blue-700">Valor Restituído</h3>
          <CreditCard className="w-5 h-5 text-blue-600" />
        </div>

        {isEditingRest && canEdit ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <MaskInputDin
                value={parseFloat(tempRestValue.replace(/\./g, '').replace(',', '.')) || 0}
                onChange={v =>
                  setTempRestValue(v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
                }
                className="text-2xl font-bold text-blue-600 border-blue-200 focus:border-blue-400"
                placeholder="0,00"
                autoFocus
              />
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={handleSaveRest} className="bg-green-600 hover:bg-green-700 text-white">
                <Check className="w-3 h-3 mr-1" />
                Salvar
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancelRest} className="border-gray-300">
                <X className="w-3 h-3 mr-1" />
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-3xl font-bold text-blue-600 mb-1">{formatCurrency(restValue)}</p>
            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-600">Valor já restituído</p>
              {canEdit && (
                <div className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                  <Edit3 className="w-3 h-3" />
                  Clique para editar
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Débito */}
      <div
        className={[
          'bg-white rounded-xl shadow-sm border-2 p-6 transition-all duration-200 relative',
          canEdit ? 'cursor-pointer' : 'cursor-default',
          isEditingDebit ? 'border-red-300 shadow-lg' : 'border-red-200 hover:border-red-300 hover:shadow-md'
        ].join(' ')}
        onClick={() => canEdit && !isEditingDebit && setIsEditingDebit(true)}
        aria-disabled={!canEdit}
      >
        {!isEditingDebit && canEdit && (
          <div className="absolute top-2 right-2 bg-red-100 rounded-full p-1">
            <Edit3 className="w-3 h-3 text-red-600" />
          </div>
        )}

        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-red-700">Débito</h3>
          <AlertTriangle className="w-5 h-5 text-red-600" />
        </div>

        {isEditingDebit && canEdit ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <MaskInputDin
                value={parseFloat(tempDebitValue.replace(/\./g, '').replace(',', '.')) || 0}
                onChange={v =>
                  setTempDebitValue(v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
                }
                className="text-2xl font-bold text-red-600 border-red-200 focus:border-red-400"
                placeholder="0,00"
                autoFocus
              />
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={handleSaveDebit} className="bg-green-600 hover:bg-green-700 text-white">
                <Check className="w-3 h-3 mr-1" />
                Salvar
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancelDebit} className="border-gray-300">
                <X className="w-3 h-3 mr-1" />
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-3xl font-bold text-red-600 mb-1">{formatCurrency(debitValue)}</p>
            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-600">Débitos pendentes</p>
              {canEdit && (
                <div className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-1 rounded-full">
                  <Edit3 className="w-3 h-3" />
                  Clique para editar
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
